import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import "./calendarpanel.css";

/* The app's date picker (design 2026-10-01): month and year dropdowns with
   arrows, a Monday-first grid with the neighbouring months' days greyed, and
   a footer with the picked date and a Today button. Drop-in for the inline
   pickers the module toolbars used: `value` (dayjs / Date / string) and
   `onChange(dayjs)`. Optional `minDate` / `maxDate` disable days outside. */

const Chevron = ({ dir }) => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
    <polyline points={dir === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
  </svg>
);

export const CalendarPanel = ({ value, onChange, minDate, maxDate, className = "" }) => {
  const selected = value ? dayjs(value) : null;
  const today = dayjs().startOf("day");
  const [view, setView] = useState((selected && selected.isValid() ? selected : today).startOf("month"));
  useEffect(() => {
    if (selected && selected.isValid()) setView(selected.startOf("month"));
    // follow outside changes of the picked month only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected && selected.format("YYYY-MM")]);

  const min = minDate ? dayjs(minDate).startOf("day") : null;
  const max = maxDate ? dayjs(maxDate).startOf("day") : null;
  const outOfRange = (d) => (min && d.isBefore(min, "day")) || (max && d.isAfter(max, "day"));

  const cells = useMemo(() => {
    const first = view.startOf("month");
    const start = first.subtract((first.day() + 6) % 7, "day");
    return Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
  }, [view]);

  const years = useMemo(() => {
    const y = view.year();
    const from = Math.min(y, today.year()) - 10;
    const to = Math.max(y, today.year()) + 5;
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
  }, [view]);

  const pick = (d) => {
    if (outOfRange(d)) return;
    onChange && onChange(d.startOf("day"));
  };
  const isTodayPicked = selected && selected.isSame(today, "day");

  return (
    <div className={`bmcal ${className}`}>
      <div className="bmcal-head">
        <button type="button" className="bmcal-nav" aria-label="Previous month" onClick={() => setView(view.subtract(1, "month"))}>
          <Chevron dir="left" />
        </button>
        <div className="bmcal-selects">
          <select
            className="bmcal-select month"
            aria-label="Month"
            value={view.month()}
            onChange={(e) => setView(view.month(Number(e.target.value)))}
          >
            {Array.from({ length: 12 }, (_, m) => (
              <option key={m} value={m}>
                {dayjs().month(m).format("MMMM")}
              </option>
            ))}
          </select>
          <select
            className="bmcal-select"
            aria-label="Year"
            value={view.year()}
            onChange={(e) => setView(view.year(Number(e.target.value)))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="bmcal-nav" aria-label="Next month" onClick={() => setView(view.add(1, "month"))}>
          <Chevron dir="right" />
        </button>
      </div>

      <div className="bmcal-grid" role="grid">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <span key={d} className="bmcal-dow">
            {d}
          </span>
        ))}
        {cells.map((d) => {
          const outside = d.month() !== view.month();
          const on = selected && d.isSame(selected, "day");
          const disabled = outOfRange(d);
          return (
            <button
              key={d.format("YYYY-MM-DD")}
              type="button"
              className={`bmcal-day${outside ? " outside" : ""}${on ? " on" : ""}${
                d.isSame(today, "day") ? " today" : ""
              }`}
              disabled={disabled}
              aria-pressed={!!on}
              aria-label={d.format("dddd, MMMM D, YYYY")}
              onClick={() => pick(d)}
            >
              {d.date()}
            </button>
          );
        })}
      </div>

      <div className="bmcal-foot">
        <span className="bmcal-picked">{selected && selected.isValid() ? selected.format("ddd, MMM D, YYYY") : "No date"}</span>
        <button
          type="button"
          className="bmcal-today"
          disabled={isTodayPicked || outOfRange(today)}
          onClick={() => {
            setView(today.startOf("month"));
            pick(today);
          }}
        >
          Today
        </button>
      </div>
    </div>
  );
};

/* A date input built on the panel: a field showing the date that opens the
   calendar below it. `value` / `onChange` use "YYYY-MM-DD" strings, like the
   native <input type="date"> it replaces. */
export const DateField = ({ value, onChange, min, max, className = "" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const d = value ? dayjs(value) : null;
  return (
    <div className={`bmcal-field ${className}`} ref={ref}>
      <button type="button" className="bmcal-field-btn" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
          <path d="M3.5 10h17M8 3v4M16 3v4" />
        </svg>
        {d && d.isValid() ? d.format("MMM D, YYYY") : "Pick a date"}
      </button>
      {open && (
        <div className="bmcal-pop" role="dialog">
          <CalendarPanel
            value={d}
            minDate={min}
            maxDate={max}
            onChange={(nd) => {
              onChange(nd.format("YYYY-MM-DD"));
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CalendarPanel;
